export type PrivacyProvisionKind =
  | "spoken"
  | "written"
  | "printed"
  | "digital"
  | "other";

export const CURRENT_PRIVACY_NOTICE_VERSION = "2026-08";

/** Bekannte Felder eines wiederverwendeten Kontakts. Fehlende Schlüssel sind unbekannt. */
export type FinderContactBaseline = {
  displayName?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  notes?: string | null;
  privacyProvisionKind?: PrivacyProvisionKind | null;
  privacyDocumented?: boolean | null;
};

export type FinderDraft = {
  clientContactId: string;
  matchedContactId: string | null;
  matchedBaseline: FinderContactBaseline | null;
  displayName: string;
  phone: string;
  email: string;
  address: string;
  notes: string;
  preferredChannel: string;
  privacyNoticeVersion: string;
  privacyProvisionKind: PrivacyProvisionKind | null;
  privacyDocumented: boolean;
};

function newClientId(): string {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const emptyFinderDraft = (): FinderDraft => ({
  clientContactId: newClientId(),
  matchedContactId: null,
  matchedBaseline: null,
  displayName: "",
  phone: "",
  email: "",
  address: "",
  notes: "",
  preferredChannel: "",
  privacyNoticeVersion: CURRENT_PRIVACY_NOTICE_VERSION,
  privacyProvisionKind: null,
  privacyDocumented: false,
});

export function contactPhoneDigits(phone: string): string {
  return (phone ?? "").replace(/[^0-9]/g, "");
}

/** Exakte Telefonnummer (>= 8 Ziffern) oder E-Mail – kein Namens-Browse. */
export function isConcreteContactMatchInput(input: {
  phone?: string | null;
  email?: string | null;
}): boolean {
  const digits = contactPhoneDigits(input.phone ?? "");
  const email = (input.email ?? "").trim().toLowerCase();
  return digits.length >= 8 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function isFinderDraftReady(draft: FinderDraft): boolean {
  return draft.displayName.trim().length > 0;
}

/** Finder-Modus: keine App-Navigation, keine Finalisierung. */
export function finderModeAllowsAppNavigation(active: boolean): boolean {
  return !active;
}

export function finderModeAllowsFinalize(active: boolean): boolean {
  return !active;
}

export const PRIVACY_NOTICE_TEXT =
  "Im Rahmen der Aufnahme werden Kontaktdaten (Name, Telefon, E-Mail, Anschrift) dokumentiert, damit die Pflegestelle bei Rückfragen zum Fundtier Kontakt aufnehmen kann. Die Angaben sind nur für berechtigte Personen der zuständigen Pflegestelle sowie Vorstand/Admin sichtbar. Diese Maske dokumentiert, dass Ihnen die Datenschutzinformation bereitgestellt wurde – sie ist keine pauschale Einwilligung.";

export const PRIVACY_PROVISION_OPTIONS: {
  value: PrivacyProvisionKind;
  label: string;
}[] = [
  { value: "spoken", label: "Mündlich" },
  { value: "written", label: "Schriftlich" },
  { value: "printed", label: "Ausdruck" },
  { value: "digital", label: "Digital" },
  { value: "other", label: "Sonstiges" },
];

export const FINDER_GATE_TITLE = "Danke für Ihre Hilfe!";

export const FINDER_GATE_INTRO =
  "Sie haben ein Wildtier gefunden. Damit wir Sie bei Fragen erreichen können, tragen Sie bitte Ihre Kontaktdaten ein. Das dauert etwa eine Minute. Danach geben Sie das Gerät zurück.";

export const FINDER_NAME_HINT =
  "Bitte tragen Sie Ihren Namen ein. Ohne Namen können wir Ihre Angaben nicht speichern.";

export const FINDER_NAME_SAVE_HINT =
  "Ohne Namen wird der Finder nicht gespeichert.";

export const FINDER_NOTES_LABEL = "Was sollen wir noch wissen?";

export const FINDER_NOTES_HELP =
  "Zum Beispiel: Wo und wie haben Sie das Tier gefunden?";

export const FINDER_FINISH_LABEL = "Fertig – Gerät zurückgeben";

export const FINDER_DISCARD_LABEL = "Ohne Angaben zurückgeben";

export const FINDER_KEEP_DIALOG_TITLE = "Sind Sie fertig?";

export const FINDER_KEEP_DIALOG_BODY =
  "Danach können Sie nichts mehr ändern. Die Pflegekraft prüft Ihre Angaben.";

export const FINDER_KEEP_CONFIRM = "Ja, fertig";

export const FINDER_CONTINUE = "Weiter ausfüllen";

export const FINDER_DROP_DIALOG_TITLE = "Ohne Angaben zurückgeben?";

export const FINDER_DROP_DIALOG_BODY =
  "Ihre Eingaben werden gelöscht. Die Pflegekraft kann Sie dann nicht erreichen.";

export const FINDER_DROP_CONFIRM = "Ja, ohne Angaben";

export const FINDER_HANDOVER_TITLE = "Vielen Dank!";

export const FINDER_HANDOVER_BODY =
  "Bitte geben Sie das Gerät jetzt der Pflegekraft zurück.";

export const FINDER_HANDOVER_ACTION = "Ich bin die Pflegekraft – weiter";

export const FINDER_GATE_CRASH = "Finder-Modus konnte nicht angezeigt werden.";

export const FINDER_GATE_END = "Finder-Modus beenden";

export const FINDER_LOCK_SIGN_OUT = "Abmelden";

export const FINDER_STATUS_RECORDED = "Finder-Angaben eingetragen";

export const FINDER_STATUS_NONE = "Keine Finder-Angaben";

export const FINDER_INTAKE_FAILED = "Aufnahme fehlgeschlagen.";

export const FINDER_BATCH_FAILED = "Sammelaufnahme fehlgeschlagen.";

export const FINDER_STILL_ACTIVE =
  "Das Gerät ist noch bei der Finder-Person. Die Aufnahme ist erst nach der Rückgabe möglich.";

export const FINDER_PRIVACY_CONFIRMED = "Datenschutzbestätigung: ja";

export const FINDER_PRIVACY_MISSING = "Datenschutzbestätigung fehlt";

/** Treffer einer anderen Person: die Bestätigung dieser Erfassung gilt nicht mehr. */
export const FINDER_MATCH_PRIVACY_RESET =
  "Andere Person gewählt, bitte Datenschutzbestätigung erneut beim Finder einholen.";

/** Der Entwurf passt nicht mehr zum gewählten Kontakt. Es entsteht ein neuer Kontakt. */
export const FINDER_MATCH_DETACHED =
  "Angaben weichen vom gewählten Kontakt ab – es wird ein neuer Kontakt angelegt.";

/** Sicherheitsnetz: der Treffer wurde serverseitig nicht fortgeschrieben. */
export const FINDER_MATCH_SAFETY_DETACHED =
  "Angaben wichen vom gewählten Kontakt ab. Es wurde ein neuer Kontakt angelegt.";

/** Bestätigung ohne gewählte Art darf nicht auf den alten Kontaktwert zurückfallen. */
export const FINDER_PROVISION_REQUIRED =
  "Bitte die Art der Bereitstellung für diese Datenschutzbestätigung wählen.";

export const FINDER_SAVED_FINDER_FAILED =
  "Der Fall wurde gespeichert. Die Finder-Angaben konnten nicht gespeichert werden.";

export const FINDER_OFFLINE_FINDER =
  "Finder-Angaben können nur online gespeichert werden.";

export const FINDER_OFFLINE_DROP_LABEL = "Ohne Finder-Angaben speichern";

export const FINDER_OFFLINE_DROP_TITLE = "Ohne Finder-Angaben speichern?";

export const FINDER_OFFLINE_DROP_BODY =
  "Die Finder-Angaben werden verworfen. Danach wird normal ohne Finder gespeichert.";

export const FINDER_OFFLINE_DROP_CONFIRM = "Ja, ohne Finder-Angaben";

export const FINDER_OFFLINE_DROP_CANCEL = "Weiter bearbeiten";

export const FINDER_LOCK_WRITE_FAILED =
  "Der Finder-Modus konnte nicht gestartet werden. Bitte erneut versuchen.";

export const FINDER_LOCK_CLEAR_FAILED =
  "Die Finder-Sperre ist in diesem Tab aufgehoben. Der Gerätespeicher konnte nicht geleert werden.";

export const FINDER_PRIVACY_SAVE_BLOCKED =
  "Finder-Kontaktdaten werden nur mit dokumentierter Datenschutzbestätigung gespeichert.";

/** Nur, wenn der Fall in diesem Versuch schon angelegt ist (Retry). */
export const FINDER_PRIVACY_SAVE_BLOCKED_SAVED =
  "Der Fall ist bereits gespeichert. Finder-Kontaktdaten werden nur mit dokumentierter Datenschutzbestätigung gespeichert.";

/** Steht am Ende der Fehlerhinweise, wenn der Upsert nach dem Link nicht angekommen ist. */
export const FINDER_EARLIER_CAPTURE_NOTE =
  "Die in der Akte angezeigten Kontaktangaben können noch von einer früheren Erfassung stammen.";

const FINDER_LINKED_UPDATE_FAILED_LEAD =
  "Verknüpft. Angaben und Bestätigung nicht gespeichert.";

function withEarlierCapture(text: string): string {
  return `${text} ${FINDER_EARLIER_CAPTURE_NOTE}`;
}

export const FINDER_LINKED_UPDATE_FAILED = withEarlierCapture(FINDER_LINKED_UPDATE_FAILED_LEAD);

/** Deutscher Zusatz hinter dem festen Satz, der Satz zur früheren Erfassung bleibt am Ende. */
export function finderLinkedUpdateFailedNotice(detail: string): string {
  return withEarlierCapture(`${FINDER_LINKED_UPDATE_FAILED_LEAD} ${detail}`);
}

/** Nach gelungenem Link, wenn das Update wegen fehlender Verbindung unklar bleibt. */
export const FINDER_LINKED_UPDATE_OFFLINE = withEarlierCapture(
  "Verknüpft. Speichern unklar (keine Verbindung).",
);

/** Batch: einige Tiere des Vorgangs hängen schon an einem Finder, der Rest nicht. */
export const FINDER_PARTIAL_IDENTITY_LOCKED =
  "Der Finder ist bereits mit einem Teil der Tiere verknüpft. Bitte erneut speichern; eine andere Person kann danach in der Akte ergänzt werden.";

/** Derselbe Hinweis, wenn die Eingabe auf die verknüpfte Person zurückgesetzt wurde. */
export const FINDER_PARTIAL_IDENTITY_RESTORED =
  "Der Finder ist bereits mit einem Teil der Tiere verknüpft. Die Eingabe wurde auf diese Person zurückgesetzt. Bitte erneut speichern; eine andere Person kann danach in der Akte ergänzt werden.";

/** Art, Version und Zeitpunkt in der Akte gehören zum Kontakt, nicht zu dieser Erfassung. */
export const FINDER_ON_CONTACT_STAND =
  "Am Kontakt hinterlegt (zuletzt gespeicherter Stand).";

export const FINDER_PRIVACY_HANDBACK = "Erneut an Finder übergeben";

export const FINDER_PRIVACY_DROP_SAVE = "Ohne Finder-Daten speichern";

export const FINDER_RELOAD_LOST =
  "Falls Finder-Angaben erfasst wurden, gingen sie beim Neuladen verloren. Bitte erneut erfassen.";

/** Gateway hat den Fall nur lokal in die Warteschlange gelegt. */
export function intakeQueuedLocally(
  result: { pendingSync?: boolean } | readonly { pendingSync?: boolean }[],
): boolean {
  const rows = Array.isArray(result) ? result : [result];
  return rows.some((row) => row.pendingSync === true);
}

const FINDER_IDENTITY_KEYS = [
  "displayName",
  "phone",
  "email",
  "address",
  "matchedContactId",
] as const;

/** Name, Kontaktweg oder wiederverwendeter Kontakt. Hinweise zählen nicht. */
export function finderIdentityChanged(
  prev: FinderDraft,
  patch: Partial<FinderDraft>,
): boolean {
  return FINDER_IDENTITY_KEYS.some(
    (key) => patch[key] !== undefined && patch[key] !== prev[key],
  );
}

function personFold(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function personPhone(value: string | null | undefined): string {
  return contactPhoneDigits(value ?? "");
}

/** Name, Telefon, E-Mail: trim, ohne Groß-/Kleinschreibung, Telefon nur Ziffern. */
export function finderPersonUnchanged(
  prev: { displayName?: string | null; phone?: string | null; email?: string | null },
  patch: Partial<FinderDraft>,
): boolean {
  const nextName = personFold(
    patch.displayName !== undefined ? patch.displayName : prev.displayName,
  );
  const nextPhone = personPhone(patch.phone !== undefined ? patch.phone : prev.phone);
  const nextEmail = personFold(patch.email !== undefined ? patch.email : prev.email);
  return (
    nextName === personFold(prev.displayName) &&
    nextPhone === personPhone(prev.phone) &&
    nextEmail === personFold(prev.email)
  );
}

/**
 * Name, Telefon oder E-Mail, normalisiert. Eine gesetzte clientContactId im Patch bleibt.
 * So trägt der Entwurf die passende ID, bevor gespeichert wird.
 */
export function applyFinderDraftPatch(
  prev: FinderDraft,
  patch: Partial<FinderDraft>,
): FinderDraft {
  const next: FinderDraft = { ...prev, ...patch };
  if (patch.clientContactId !== undefined) return next;
  if (!finderPersonUnchanged(prev, patch)) next.clientContactId = newClientId();
  return next;
}

export function finderProvisionLabel(kind: PrivacyProvisionKind | null): string {
  if (!kind) return "–";
  return PRIVACY_PROVISION_OPTIONS.find((option) => option.value === kind)?.label ?? "–";
}

export function finderDraftHasName(draft: FinderDraft): boolean {
  return draft.displayName.trim().length > 0;
}

/** Name, Telefon, E-Mail, Anschrift oder ein wiederverwendeter Kontakt. */
export function finderHasContactData(draft: FinderDraft): boolean {
  return (
    finderDraftHasName(draft) ||
    draft.phone.trim().length > 0 ||
    draft.email.trim().length > 0 ||
    draft.address.trim().length > 0 ||
    draft.preferredChannel.trim().length > 0 ||
    Boolean(draft.matchedContactId?.trim())
  );
}

/** Kontaktdaten ohne dokumentierte Datenschutzbestätigung dürfen nicht persistiert werden. */
export function finderSaveBlockedWithoutPrivacy(draft: FinderDraft): boolean {
  return finderHasContactData(draft) && !draft.privacyDocumented;
}

/** Bestätigte Datenschutzinformation ohne ausdrücklich gewählte Bereitstellungsart. */
export function finderSaveBlockedWithoutProvision(draft: FinderDraft): boolean {
  return (
    draft.privacyDocumented &&
    finderHasContactData(draft) &&
    draft.privacyProvisionKind == null
  );
}

/**
 * Treffer lösen: neuer Kontakt, neue clientContactId.
 * Eine im Patch gesetzte clientContactId bleibt (Zurücksetzen nach Teil-Link).
 */
export function detachFinderMatch(
  draft: FinderDraft,
  options?: { keepClientContactId?: boolean },
): FinderDraft {
  return {
    ...draft,
    matchedContactId: null,
    matchedBaseline: null,
    clientContactId: options?.keepClientContactId ? draft.clientContactId : newClientId(),
  };
}

/**
 * Identitätsvergleich zum Treffer.
 * Ein leeres Entwurfsfeld ist unbekannt, nicht abweichend, nur wenn der Name
 * normalisiert gleich ist, mindestens E-Mail oder Telefon gleich und gefüllt ist
 * und kein gefülltes Feld widerspricht. Sonst ist es eine Abweichung.
 * Ohne Baseline gibt es nichts, wovon abgewichen werden kann.
 */
export function finderMatchBaselineDeviates(
  draft: {
    displayName?: string | null;
    phone?: string | null;
    email?: string | null;
  },
  baseline: FinderContactBaseline | null | undefined,
): boolean {
  if (!baseline) return false;
  const name = personFold(draft.displayName);
  const baseName = personFold(baseline.displayName);
  if (name !== baseName) return true;
  const phone = personPhone(draft.phone);
  const basePhone = personPhone(baseline.phone);
  const email = personFold(draft.email);
  const baseEmail = personFold(baseline.email);
  if (phone.length > 0 && phone !== basePhone) return true;
  if (email.length > 0 && email !== baseEmail) return true;
  const phoneAnchors = phone.length > 0 && phone === basePhone;
  const emailAnchors = email.length > 0 && email === baseEmail;
  return !phoneAnchors && !emailAnchors;
}

function baselineHas(
  baseline: FinderContactBaseline,
  key: keyof FinderContactBaseline,
): boolean {
  return Object.prototype.hasOwnProperty.call(baseline, key);
}

function writtenText(value: string | null | undefined): string {
  return (value ?? "").trim();
}

/** Baseline aus den Werten, die der Entwurf tatsächlich übernimmt. */
export function finderMatchedBaseline(
  applied: { displayName: string; phone?: string | null; email?: string | null },
  privacyDocumented: boolean,
): FinderContactBaseline {
  return {
    displayName: applied.displayName,
    phone: applied.phone ?? "",
    email: applied.email ?? "",
    privacyDocumented,
  };
}

/**
 * Wiederverwendung: true, wenn der Entwurf gegenüber dem bekannten Kontakt nichts schreibt.
 * Unbekannte Felder zählen nur als Änderung, wenn der Entwurf dort einen Wert setzt.
 * Eine neue Datenschutzbestätigung ist immer eine Änderung.
 */
export function finderReuseUnchanged(
  next: {
    displayName?: string | null;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    notes?: string | null;
    privacyProvisionKind?: PrivacyProvisionKind | null;
    privacyDocumented?: boolean | null;
  },
  baseline: FinderContactBaseline | null | undefined,
): boolean {
  if (!baseline) return false;
  if (next.privacyDocumented) return false;
  const textKeys = ["displayName", "phone", "email", "address", "notes"] as const;
  for (const key of textKeys) {
    const written = writtenText(next[key]);
    if (!baselineHas(baseline, key)) {
      if (written.length > 0) return false;
      continue;
    }
    if (written !== writtenText(baseline[key])) return false;
  }
  if (!baselineHas(baseline, "privacyProvisionKind")) {
    if (next.privacyProvisionKind != null) return false;
  } else if ((next.privacyProvisionKind ?? null) !== (baseline.privacyProvisionKind ?? null)) {
    return false;
  }
  if (
    baselineHas(baseline, "privacyDocumented") &&
    Boolean(baseline.privacyDocumented) !== Boolean(next.privacyDocumented)
  ) {
    return false;
  }
  return true;
}

/** Irgendetwas außer dem leeren Pflichtnamen: Kontakt, Hinweise oder Datenschutz-Doku. */
export function finderDraftHasPayload(draft: FinderDraft): boolean {
  return (
    finderDraftHasName(draft) ||
    draft.phone.trim().length > 0 ||
    draft.email.trim().length > 0 ||
    draft.address.trim().length > 0 ||
    draft.notes.trim().length > 0 ||
    draft.preferredChannel.trim().length > 0 ||
    draft.privacyDocumented ||
    draft.privacyProvisionKind != null
  );
}

/** Kontakt oder Datenschutz-Doku ohne Namen würden beim Speichern still verloren gehen. */
export function finderPayloadWithoutName(draft: FinderDraft): boolean {
  return finderDraftHasPayload(draft) && !finderDraftHasName(draft);
}

export function finderMissingContactLabels(draft: FinderDraft): string[] {
  const missing: string[] = [];
  if (!draft.phone.trim()) missing.push("Telefon");
  if (!draft.email.trim()) missing.push("E-Mail");
  if (!draft.address.trim()) missing.push("Anschrift");
  return missing;
}

/** Android-Zurück im Formular: nie direkt beenden. */
export function finderHardwareBackPhase(
  draft: FinderDraft
): "confirmKeep" | "confirmDrop" | "handover" {
  if (finderDraftHasName(draft)) return "confirmKeep";
  if (finderDraftHasPayload(draft)) return "confirmDrop";
  return "handover";
}

export function finderShowsBottomBar(isWide: boolean, finderActive: boolean): boolean {
  return !isWide && !finderActive;
}

export function finderBackgroundLockProps(
  active: boolean,
  os: string
): {
  accessibilityElementsHidden?: boolean;
  importantForAccessibility?: "auto" | "no-hide-descendants";
  "aria-hidden"?: boolean;
  inert?: boolean;
} {
  if (!active) return {};
  return {
    accessibilityElementsHidden: true,
    importantForAccessibility: "no-hide-descendants",
    ...(os === "web" ? { "aria-hidden": true, inert: true } : {}),
  };
}
